import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { get } from "lodash";
import { putUserDetails, changeEmailOtp, changePhoneOtp, verifySsoCode } from "../../../helper/utils";
import { setUserDetails } from "../../../store/actions";
import "./index.m.css";
import { UserIcon, PhoneIcon, EmailIcon, TimerIcon, SpinnerIcon } from "./icons";

export const OnboardModal = ({ onClose }) => {
  const dispatch = useDispatch();
  const reduxUserDetails = useSelector((state) => get(state, ["user", "user"], {}));
  const isGoogleUser = reduxUserDetails?.metadata?.login_method === "google_login" || reduxUserDetails?.metadata?.login_method === "google";
  const [isSubmittingOnboarding, setIsSubmittingOnboarding] = useState(false);
  const ssoEndpoint = useSelector((state) => get(state, ["qt", "config", "publisher-attributes", "sso_endpoint"], ""));

  let userdetails = reduxUserDetails;

  useEffect(() => {
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, []);

  if (typeof window !== "undefined" && Object.keys(reduxUserDetails).length === 0) {
    try {
      const localUser = window.localStorage.getItem("user");
      if (localUser) {
        userdetails = JSON.parse(localUser);
      }
    } catch (e) {
      console.error(e);
    }
  }

  const userData = userdetails?.userData || userdetails || {};
  const userMetadata = userdetails?.metadata || {};

  const initialMobileRaw = userData?.phoneNumbers?.[0] || userdetails?.phoneNumber || "";
  let initialMobile = initialMobileRaw.replace(/^\+91/, "");
  if (initialMobile.length > 10 && initialMobile.startsWith("91")) {
    initialMobile = initialMobile.substring(2);
  }
  const initialEmail = userData?.emails?.[0] || userdetails?.email || "";
  const initialFirstName = userMetadata?.first_name || "";

  const [mobileNumber, setMobileNumber] = useState(initialMobile || "");
  const [savedMobile, setSavedMobile] = useState(initialMobile);
  const [mobileError, setMobileError] = useState(false);
  const [mobileErrorMessage, setMobileErrorMessage] = useState("");
  const [otpSent, setOtpSent] = useState(true);
  const [timer, setTimer] = useState(30);
  const [otpValue, setOtpValue] = useState("");
  const [otpError, setOtpError] = useState(false);
  const [otpVerified, setOtpVerified] = useState(!!initialMobile);
  const [isVerifying, setIsVerifying] = useState(false);

  const [name, setName] = useState(initialFirstName);
  const [savedName, setSavedName] = useState(initialFirstName);
  const [nameDone, setNameDone] = useState(!!initialFirstName);
  const [isUpdatingName, setIsUpdatingName] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [nameErrorMessage, setNameErrorMessage] = useState("");

  const [email, setEmail] = useState(initialEmail);
  const [savedEmail, setSavedEmail] = useState(initialEmail);
  const [emailError, setEmailError] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailTimer, setEmailTimer] = useState(30);
  const [emailOtpValue, setEmailOtpValue] = useState("");
  const [emailOtpError, setEmailOtpError] = useState(false);
  const [emailOtpVerified, setEmailOtpVerified] = useState(!!initialEmail);
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);

  const [authSessionInfo, setAuthSessionInfo] = useState({ preAuthSessionId: null, deviceId: null });
  const isMobileValid = !mobileNumber || String(mobileNumber || "").trim().length === 0 || otpVerified;
  const isEmailValid = !email || String(email || "").trim().length === 0 || emailOtpVerified;
  const hasAtLeastOneContact = (mobileNumber && String(mobileNumber || "").trim().length > 0) || (email && String(email || "").trim().length > 0);
  const isNameValidForSubmit = nameDone && savedName && String(savedName || "").trim().length >= 2 && !/^\d+$/.test(String(savedName || "").trim());
  const isFormComplete = isNameValidForSubmit && isMobileValid && isEmailValid && hasAtLeastOneContact;

  useEffect(() => {
    if (initialMobile && mobileNumber !== initialMobile && !savedMobile) {
      setMobileNumber(initialMobile);
      setSavedMobile(initialMobile);
      setOtpVerified(true);
    }
    if (initialEmail && email !== initialEmail && !savedEmail) {
      setEmail(initialEmail);
      setSavedEmail(initialEmail);
      setEmailOtpVerified(true);
    }
    if (initialFirstName && name !== initialFirstName && !savedName) {
      setName(initialFirstName);
      setSavedName(initialFirstName);
      setNameDone(true);
    }
  }, [initialMobile, initialEmail, initialFirstName]);

  useEffect(() => {
    let interval;
    if (otpSent && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpSent, timer]);

  useEffect(() => {
    let interval;
    if (emailOtpSent && emailTimer > 0) {
      interval = setInterval(() => {
        setEmailTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [emailOtpSent, emailTimer]);

  const isNameChanged = name !== savedName;
  const isNameValid = String(name || "").trim().length >= 2 && !/^\d+$/.test(String(name || "").trim());
  const isNameUpdateDisabled = !isNameChanged || !isNameValid || isUpdatingName;

  const handleNameBlur = () => {
    if (name) {
      setName(String(name || "").trim());
    }
    if (name && (String(name || "").trim().length < 2 || /^\d+$/.test(String(name || "").trim()))) {
      setNameError(true);
      setNameErrorMessage("Please enter at least 2 characters.");
    } else {
      setNameError(false);
    }
  };

  const handleUpdateName = () => {
    if (isNameUpdateDisabled) return;

    const trimmedName = String(name || "").trim();
    setName(trimmedName);

    if (/\d/.test(trimmedName)) {
      setNameError(true);
      setNameErrorMessage("Please enter a valid name.");
      return;
    }

    setIsUpdatingName(true);
    setNameError(false);
    setNameErrorMessage("");
    putUserDetails({ first_name: trimmedName })
      .then((res) => {
        setIsUpdatingName(false);
        if (res && !res.errorMessage) {
          setSavedName(trimmedName);
          setNameDone(true);

          const updatedUser = { ...userdetails, metadata: { ...userdetails?.metadata, first_name: trimmedName } };
          dispatch(setUserDetails(updatedUser));
          if (typeof window !== "undefined") {
            window.localStorage.setItem("user", JSON.stringify(updatedUser));
            window.dispatchEvent(
              new CustomEvent("show-snackbar", { detail: { type: "success", message: "Name updated successfully." } })
            );
          }
        } else {
          setNameError(true);
          const isGoogleUser =
            userdetails?.metadata?.login_method === "google_login" || userdetails?.metadata?.login_method === "google";
          const errMsg = isGoogleUser
            ? "Name is not editable for Google login"
            : "Failed to update name. Please try again.";
          setNameErrorMessage(errMsg);
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("show-snackbar", { detail: { type: "failure", message: errMsg } }));
          }

        }
      })
      .catch((err) => {
        setIsUpdatingName(false);
        setNameError(true);
        const isGoogleUser =
          userdetails?.metadata?.login_method === "google_login" || userdetails?.metadata?.login_method === "google";
        const errMsg = isGoogleUser
          ? "Name is not editable for Google login"
          : "Failed to update name. Please try again.";
        setNameErrorMessage(errMsg);
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("show-snackbar", { detail: { type: "failure", message: errMsg } }));
        }

      });
  };

  const handleMobileChange = (e) => {
    const val = e.target.value;
    setMobileNumber(val);
    if (mobileError) {
      setMobileError(false);
      setMobileErrorMessage("");
    }

    if (val === savedMobile && savedMobile) {
      setOtpVerified(true);
      setOtpSent(false);
      setOtpError(false);
    } else {
      setOtpVerified(false);
      setOtpSent(false);
      setOtpValue("");
      setOtpError(false);
      setTimer(30);
    }
  };

  const handleMobileBlur = () => {
    if (!mobileNumber || String(mobileNumber || "").trim().length === 0) {
      if (!email || String(email || "").trim().length === 0) {
        setMobileError(true);
        setMobileErrorMessage("Either mobile number or email is required");
      } else {
        setMobileError(false);
        setMobileErrorMessage("");
      }
    } else if (!/^\d{10}$/.test(mobileNumber)) {
      setMobileError(true);
      setMobileErrorMessage("Enter a valid 10-digit indian mobile number");
    } else {
      setMobileError(false);
      setMobileErrorMessage("");
    }
  };

  const isSendOtpDisabled = mobileNumber === savedMobile || !/^\d{10}$/.test(mobileNumber);

  const handleSendOtp = () => {
    if (!isSendOtpDisabled) {
      changePhoneOtp(mobileNumber)
        .then((res) => {
          if (res?.status === "OK" || res?.preAuthSessionId) {
            setAuthSessionInfo({ preAuthSessionId: res?.preAuthSessionId, deviceId: res?.deviceId });
            setOtpSent(true);
            setTimer(30);
            setMobileError(false);
            setMobileErrorMessage("");
            setOtpValue("");
            if (typeof window !== "undefined") {
              window.dispatchEvent(
                new CustomEvent("show-snackbar", {
                  detail: { type: "info", message: "OTP sent to your Mobile Number" }
                })
              );
            }
          } else {
            setMobileError(true);
            setMobileErrorMessage(res?.message || "Failed to send OTP.");
          }
        })
        .catch((err) => {
          setMobileError(true);
          if (err?.status === 409) {
            setMobileErrorMessage("User already exist with given Phone Number");
          } else {
            setMobileErrorMessage(err?.message || "Failed to send OTP.");
          }
        });
    } else {
      setMobileError(true);
      setMobileErrorMessage("Enter a valid 10-digit indian mobile number");
    }
  };

  const handleResendOtp = () => {
    if (timer === 0) {
      changePhoneOtp(mobileNumber)
        .then((res) => {
          if (res?.status === "OK" || res?.preAuthSessionId) {
            setAuthSessionInfo({ preAuthSessionId: res?.preAuthSessionId, deviceId: res?.deviceId });
            setTimer(30);
            setOtpError(false);
            setOtpValue("");
            if (typeof window !== "undefined") {
              window.dispatchEvent(
                new CustomEvent("show-snackbar", {
                  detail: { type: "info", message: "OTP sent to your Mobile Number" }
                })
              );
            }
          }
        })
        .catch(() => setOtpError(true));
    }
  };

  const handleVerifyOtp = () => {
    if (isVerifying || otpValue.length !== 6) return;
    setIsVerifying(true);

    verifySsoCode({ preAuthSessionId: authSessionInfo.preAuthSessionId, deviceId: authSessionInfo.deviceId, userInputCode: otpValue })
      .then((res) => {
        setIsVerifying(false);
        if (res?.status === "OK") {
          setOtpError(false);
          setOtpVerified(true);
          setSavedMobile(mobileNumber);
          setOtpSent(false);

          const updatedUser = {
            ...userdetails,
            userData: { ...userdetails?.userData, phoneNumbers: [`+91${mobileNumber}`] }
          };
          dispatch(setUserDetails(updatedUser));
          if (typeof window !== "undefined") {
            window.localStorage.setItem("user", JSON.stringify(updatedUser));
            window.dispatchEvent(
              new CustomEvent("show-snackbar", {
                detail: { type: "success", message: "Mobile number updated successfully." }
              })
            );
          }
        } else {
          setOtpError(true);

        }
      })
      .catch((err) => {
        setIsVerifying(false);
        setOtpError(true);

      });
  };

  const handleOtpChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
    setOtpValue(val);
    if (otpError) setOtpError(false);
  };

  const handleEmailChange = (e) => {
    const val = e.target.value;
    setEmail(val);
    if (emailError) {
      setEmailError(false);
      setEmailErrorMessage("");
    }

    if (val === savedEmail && savedEmail) {
      setEmailOtpVerified(true);
      setEmailOtpSent(false);
    } else {
      setEmailOtpVerified(false);
      setEmailOtpSent(false);
      setEmailOtpValue("");
      setEmailTimer(30);
    }
  };

  const handleEmailBlur = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || String(email || "").trim().length === 0) {
      if (!mobileNumber || String(mobileNumber || "").trim().length === 0) {
        setEmailError(true);
        setEmailErrorMessage("Either mobile number or email is required");
      } else {
        setEmailError(false);
        setEmailErrorMessage("");
      }
    } else if (!emailRegex.test(email)) {
      setEmailError(true);
      setEmailErrorMessage("Invalid email address.");
    } else {
      setEmailError(false);
      setEmailErrorMessage("");
    }
  };

  const isSendEmailOtpDisabled = email === savedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSendEmailOtp = () => {
    if (!isSendEmailOtpDisabled) {
      changeEmailOtp(email)
        .then((res) => {
          if (res?.status === "OK" || res?.preAuthSessionId) {
            setAuthSessionInfo({ preAuthSessionId: res?.preAuthSessionId, deviceId: res?.deviceId });
            setEmailOtpSent(true);
            setEmailTimer(30);
            setEmailError(false);
            setEmailErrorMessage("");
            setEmailOtpValue("");
            if (typeof window !== "undefined") {
              window.dispatchEvent(
                new CustomEvent("show-snackbar", { detail: { type: "info", message: "OTP sent to your Email ID" } })
              );
            }
          } else {
            setEmailError(true);
            setEmailErrorMessage(res?.message || "Failed to send OTP.");
          }
        })
        .catch((err) => {
          setEmailError(true);
          if (err?.status === 409) {
            setEmailErrorMessage("User already exist with given Email ID.");
          } else {
            setEmailErrorMessage(err?.message || "Failed to send OTP.");
          }
        });
    } else {
      setEmailError(true);
      setEmailErrorMessage("Invalid email address.");
    }
  };

  const handleResendEmailOtp = () => {
    if (emailTimer === 0) {
      changeEmailOtp(email)
        .then((res) => {
          if (res?.status === "OK" || res?.preAuthSessionId) {
            setAuthSessionInfo({ preAuthSessionId: res?.preAuthSessionId, deviceId: res?.deviceId });
            setEmailTimer(30);
            setEmailOtpError(false);
            setEmailOtpValue("");
            if (typeof window !== "undefined") {
              window.dispatchEvent(
                new CustomEvent("show-snackbar", { detail: { type: "info", message: "OTP sent to your Email ID" } })
              );
            }
          }
        })
        .catch(() => setEmailOtpError(true));
    }
  };

  const handleVerifyEmailOtp = () => {
    if (isVerifyingEmail || emailOtpValue.length !== 6) return;
    setIsVerifyingEmail(true);

    verifySsoCode({ preAuthSessionId: authSessionInfo.preAuthSessionId, deviceId: authSessionInfo.deviceId, userInputCode: emailOtpValue })
      .then((res) => {
        setIsVerifyingEmail(false);
        if (res?.status === "OK") {
          setEmailOtpError(false);
          setEmailOtpVerified(true);
          setSavedEmail(email);
          setEmailOtpSent(false);

          const updatedUser = { ...userdetails, userData: { ...userdetails?.userData, emails: [email] } };
          dispatch(setUserDetails(updatedUser));
          if (typeof window !== "undefined") {
            window.localStorage.setItem("user", JSON.stringify(updatedUser));
            window.dispatchEvent(
              new CustomEvent("show-snackbar", {
                detail: { type: "success", message: "Email ID updated successfully." }
              })
            );
          }
        } else {
          setEmailOtpError(true);

        }
      })
      .catch((err) => {
        setIsVerifyingEmail(false);
        setEmailOtpError(true);

      });
  };

  const handleEmailOtpChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
    setEmailOtpValue(val);
    if (emailOtpError) setEmailOtpError(false);
  };

  const handleStart = () => {
    if (!isFormComplete || isSubmittingOnboarding) return;

    setIsSubmittingOnboarding(true);
    putUserDetails({
      tpml_onboarding_completed: true,
      tpml_onboarding_completed_at: new Date().toISOString()
    })
      .then((res) => {
        if (res && !res.errorMessage) {
          const updatedUser = {
            ...userdetails,
            metadata: {
              ...userdetails?.metadata,
              tpml_onboarding_completed: true,
              tpml_onboarding_completed_at: new Date().toISOString()
            }
          };
          dispatch(setUserDetails(updatedUser));
          if (typeof window !== "undefined") {
            window.localStorage.setItem("user", JSON.stringify(updatedUser));
            onClose();
          }
        } else {
          setIsSubmittingOnboarding(false);
        }
      })
      .catch((err) => {
        setIsSubmittingOnboarding(false);

      });
  };

  return (
    <div styleName="overlay">
      <div styleName="popupContainer" onClick={(e) => e.stopPropagation()}>
        <h2 styleName="title">Welcome to <span style={{ color: "#FFD34D" }}>DH Premium</span></h2>

        <p styleName="description">
          Get more from your subscription. Please share a few details so we can keep you updated.
        </p>

        <div styleName="formSection">
          <div styleName="inputGroup">
            <div styleName="inputLabelRow">
              <UserIcon styleName="inputIcon" />
              <span styleName="inputLabel">Name *</span>
            </div>
            <input
              type="text"
              placeholder="Enter Your Name"
              styleName="inputField"
              value={name}
              readOnly={isGoogleUser}
              onClick={() => {
                if (isGoogleUser && typeof window !== "undefined") {
                  window.dispatchEvent(new CustomEvent("show-snackbar", { detail: { type: "failure", message: "Name is not editable for Google login" } }));
                }
              }}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) {
                  setNameError(false);
                  setNameErrorMessage("");
                }
                if (nameDone) setNameDone(false);
              }}
              onBlur={handleNameBlur}
            />
            <div styleName={nameError ? "inputUnderlineError" : "inputUnderline"} />
            {nameError && !isUpdatingName && (
              <div styleName="errorMessage" style={{ marginTop: "5px" }}>
                {nameErrorMessage}
              </div>
            )}

            {name && String(name || "").trim().length > 0 && !nameDone && (
              <div styleName="otpButtonContainer" style={{ gap: "10px" }}>
                <button
                  styleName={
                    isUpdatingName ? "nameDoneBtnLoading" : isNameUpdateDisabled ? "nameDoneBtnDisabled" : "nameDoneBtn"
                  }
                  onClick={handleUpdateName}
                  disabled={isNameUpdateDisabled}
                >
                  {isUpdatingName ? (
                    <>
                      <SpinnerIcon styleName="spinner" />
                      Updating
                    </>
                  ) : (
                    "Update"
                  )}
                </button>
                <button
                  styleName="nameCancelBtn"
                  onClick={() => {
                    setName(savedName || "");
                    setNameDone(true);
                    setNameError(false);
                    setNameErrorMessage("");
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          <div styleName={otpSent ? "inputGroup inputGroupReducedMargin" : "inputGroup"}>
            <div styleName="inputLabelRow">
              <PhoneIcon styleName="inputIcon" />
              <span styleName="inputLabel">Mobile Number</span>
            </div>
            {/* Using a margin bottom on inputField to represent the space even without placeholder */}
            <div styleName="phoneInputContainer" data-has-value={mobileNumber.length > 0}>
              {mobileNumber.length > 0 && <div styleName="countryCode">+91</div>}
              <input
                type="tel"
                placeholder="Enter Your Mobile Number"
                styleName="inputField phoneInputField"
                value={mobileNumber}
                onChange={handleMobileChange}
                onBlur={handleMobileBlur}
              />
            </div>
            <div styleName={mobileError && !otpSent ? "inputUnderlineError" : "inputUnderline"} />

            {mobileError && !otpSent && (
              <div styleName="errorMessage">{mobileErrorMessage || "Enter a valid 10-digit indian mobile number"}</div>
            )}

            {mobileNumber && mobileNumber.length > 0 && !otpVerified && (
              <>
                {otpSent && (
                  <div styleName="otpInputWrapper">
                    <input
                      type="text"
                      placeholder="Enter OTP"
                      styleName="inputField otpInputField"
                      value={otpValue}
                      maxLength={6}
                      onChange={handleOtpChange}
                    />
                    <div styleName={otpError ? "inputUnderlineError" : "inputUnderline"} />
                  </div>
                )}

                {otpSent && otpError && <div styleName="errorMessage">Incorrect OTP. Please try again.</div>}

                <div styleName="otpButtonContainer" style={{ gap: otpSent ? "4px" : "10px" }}>
                  <button
                    styleName={
                      otpSent
                        ? isVerifying
                          ? "otpVerifyBtnLoading"
                          : otpValue.length !== 6
                            ? "otpVerifyBtnDisabled"
                            : "otpVerifyBtn"
                        : isSendOtpDisabled
                          ? "otpResendBtnDisabled"
                          : "otpResendBtn"
                    }
                    onClick={otpSent ? handleVerifyOtp : handleSendOtp}
                    disabled={otpSent ? isVerifying || otpValue.length !== 6 : isSendOtpDisabled}
                  >
                    {otpSent ? (
                      isVerifying ? (
                        <>
                          <SpinnerIcon styleName="spinner" />
                          Verifying
                        </>
                      ) : (
                        "Verify OTP"
                      )
                    ) : (
                      "Send OTP"
                    )}
                  </button>
                  <button
                    styleName="otpCancelBtn"
                    onClick={() => {
                      setMobileNumber(savedMobile || "");
                      setMobileError(false);
                      setOtpSent(false);
                      setTimer(30);
                      setOtpError(false);
                      setOtpValue("");
                      setOtpVerified(!!savedMobile);
                    }}
                  >
                    Cancel
                  </button>
                </div>

                {otpSent && (
                  <div styleName="resendContainer">
                    {timer > 0 ? (
                      <div styleName="resendBlock">
                        <span styleName="resendTextDisabled">Resend OTP</span>
                        <div styleName="timerWrapper">
                          <TimerIcon />
                          <span styleName="timerCount">{timer}</span>
                        </div>
                      </div>
                    ) : (
                      <span styleName="resendTextActive" onClick={handleResendOtp}>
                        Resend OTP
                      </span>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <div styleName={emailOtpSent ? "inputGroup inputGroupReducedMargin" : "inputGroup"}>
            <div styleName="inputLabelRow">
              <EmailIcon styleName="inputIcon" />
              <span styleName="inputLabel">Email *</span>
            </div>
            <input
              type="email"
              placeholder="Enter Your Email Address"
              styleName="inputField emailInputField"
              value={email}
              readOnly={isGoogleUser}
              onClick={() => {
                if (isGoogleUser && typeof window !== "undefined") {
                  window.dispatchEvent(new CustomEvent("show-snackbar", { detail: { type: "failure", message: "Email ID is not editable when logged in via Google." } }));
                }
              }}
              onChange={handleEmailChange}
              onBlur={handleEmailBlur}
              onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
            />
            <div styleName={emailError && !emailOtpSent ? "inputUnderlineError" : "inputUnderline"} />

            {email && email.length > 0 && !emailOtpVerified && (
              <>
                {emailOtpSent && (
                  <div styleName="otpInputWrapper">
                    <input
                      type="text"
                      placeholder="Enter OTP"
                      styleName="inputField otpInputField"
                      value={emailOtpValue}
                      maxLength={6}
                      onChange={handleEmailOtpChange}
                    />
                    <div styleName={emailOtpError ? "inputUnderlineError" : "inputUnderline"} />
                  </div>
                )}

                {emailOtpSent && emailOtpError && <div styleName="errorMessage">Incorrect OTP. Please try again.</div>}
                {emailError && !emailOtpSent && (
                  <div styleName="errorMessage">{emailErrorMessage || "Invalid email address."}</div>
                )}

                <div styleName="otpButtonContainer" style={{ gap: emailOtpSent ? "4px" : "10px" }}>
                  <button
                    styleName={
                      emailOtpSent
                        ? isVerifyingEmail
                          ? "otpVerifyBtnLoading"
                          : emailOtpValue.length !== 6
                            ? "otpVerifyBtnDisabled"
                            : "otpVerifyBtn"
                        : isSendEmailOtpDisabled
                          ? "otpResendBtnDisabled"
                          : "otpResendBtn"
                    }
                    onClick={emailOtpSent ? handleVerifyEmailOtp : handleSendEmailOtp}
                    disabled={emailOtpSent ? isVerifyingEmail || emailOtpValue.length !== 6 : isSendEmailOtpDisabled}
                  >
                    {emailOtpSent ? (
                      isVerifyingEmail ? (
                        <>
                          <SpinnerIcon styleName="spinner" />
                          Verifying
                        </>
                      ) : (
                        "Verify OTP"
                      )
                    ) : (
                      "Send OTP"
                    )}
                  </button>
                  <button
                    styleName="otpCancelBtn"
                    onClick={() => {
                      setEmail(savedEmail || "");
                      setEmailError(false);
                      setEmailOtpSent(false);
                      setEmailTimer(30);
                      setEmailOtpError(false);
                      setEmailOtpValue("");
                      setEmailOtpVerified(!!savedEmail);
                    }}
                  >
                    Cancel
                  </button>
                </div>

                {emailOtpSent && (
                  <div styleName="resendContainer">
                    {emailTimer > 0 ? (
                      <div styleName="resendBlock">
                        <span styleName="resendTextDisabled">Resend OTP</span>
                        <div styleName="timerWrapper">
                          <TimerIcon />
                          <span styleName="timerCount">{emailTimer}</span>
                        </div>
                      </div>
                    ) : (
                      <span styleName="resendTextActive" onClick={handleResendEmailOtp}>
                        Resend OTP
                      </span>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <button
            styleName={isFormComplete ? "ctaButton ctaButtonActive" : "ctaButton"}
            onClick={handleStart}
            disabled={!isFormComplete || isSubmittingOnboarding}
          >
            {isSubmittingOnboarding ? "Saving" : "Get Started"}
          </button>
        </div>
      </div>
    </div>
  );
};
